/**
 * @page /portal/schulungen (MA-Portal: Schulungen-Liste)
 * @description Listet alle dem MA zugewiesenen Schulungen + Tests.
 * @api GET /api/portal/schulungen
 * @roles portal_mitarbeiter
 * @i18n qmTest.*
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Award, CheckCircle2, AlertCircle, Clock, ChevronRight, ArrowLeft } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const STATUS_META = {
  offen:           { color: '#338edc', bg: '#EFF6FF',  labelKey: 'qmTest.status.offen',           labelDef: 'Offen' },
  tour_offen:      { color: '#338edc', bg: '#EFF6FF',  labelKey: 'qmTest.status.tour_offen',      labelDef: 'Tour offen' },
  test_offen:      { color: '#F59E0B', bg: '#FFFBEB',  labelKey: 'qmTest.status.test_offen',      labelDef: 'Test offen' },
  bestanden:       { color: '#10B981', bg: '#ECFDF5',  labelKey: 'qmTest.status.bestanden',       labelDef: 'Bestanden' },
  nicht_bestanden: { color: '#EF4444', bg: '#FEF2F2',  labelKey: 'qmTest.status.nicht_bestanden', labelDef: 'Nicht bestanden' },
  abgelaufen:      { color: '#6B7280', bg: '#F3F4F6',  labelKey: 'qmTest.status.abgelaufen',      labelDef: 'Abgelaufen' },
};

export default function PortalSchulungen() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/portal/schulungen`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-app, #F8F9FC)' }}>
      <div className="px-4 py-4 flex items-center gap-3 border-b" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #FFFFFF)' }}>
        <button
          onClick={() => navigate('/portal')}
          className="p-1.5 rounded-lg hover:bg-[var(--bg-inset,#F8F9FC)]"
          aria-label="Zurück"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} style={{ color: 'var(--text-primary, #1A1A2E)' }} />
        </button>
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5" strokeWidth={1.5} style={{ color: '#338edc' }} />
          <h1 className="text-base font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {t('qmTest.portalTitle') || 'Meine Schulungen'}
          </h1>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {loading ? (
          <div className="text-center py-10 text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            {t('qmTest.loading') || 'Lade …'}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl p-8 text-center" style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}>
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3" strokeWidth={1.5} style={{ color: '#10B981' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('qmTest.noSchulungen') || 'Keine offenen Schulungen'}
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('qmTest.noSchulungenHint') || 'Du hast aktuell keine zugewiesenen Tests.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(it => {
              const meta = STATUS_META[it.status] || STATUS_META.offen;
              const ueberfaellig = it.faellig_bis && new Date(it.faellig_bis) < new Date() && !['bestanden','abgelaufen'].includes(it.status);
              return (
                <Link
                  key={it.id}
                  to={`/portal/schulungen/${it.id}`}
                  className="block rounded-2xl p-4 transition-all active:scale-[0.99]"
                  style={{ background: 'var(--bg-card, #FFFFFF)', border: '1px solid var(--border-card, #E5E7EB)' }}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: meta.bg }}>
                      <Award className="w-5 h-5" strokeWidth={1.5} style={{ color: meta.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                        {it.test_titel || it.prozess_titel}
                      </p>
                      <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                        {it.prozess_titel}
                      </p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: meta.bg, color: meta.color }}>
                          {t(meta.labelKey) || meta.labelDef}
                        </span>
                        {it.faellig_bis && (
                          <span className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${ueberfaellig ? 'bg-rose-50 text-rose-700' : ''}`}
                            style={!ueberfaellig ? { background: 'var(--bg-inset, #F3F4F6)', color: 'var(--text-tertiary, #6B7280)' } : undefined}>
                            <Clock className="w-3 h-3" strokeWidth={1.5} />
                            {formatDate(it.faellig_bis)}
                          </span>
                        )}
                        {it.versuche_genutzt > 0 && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-inset, #F3F4F6)', color: 'var(--text-tertiary, #6B7280)' }}>
                            {it.versuche_genutzt}/{it.max_versuche} {t('qmTest.versuche') || 'Versuche'}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 shrink-0 mt-1" strokeWidth={1.5} style={{ color: 'var(--text-tertiary, #9CA3AF)' }} />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
